/**
 * TypeScript types for chat interface, matching Python Pydantic models.
 * Source: src/space_operator/models/chat.py
 */

export enum MessageRole {
  USER = 'user',
  AGENT = 'agent',
  SYSTEM = 'system',
}

export enum ToolStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETE = 'complete',
  ERROR = 'error',
}

export enum ArtifactType {
  PLOT = 'plot',
  DATA = 'data',
  ANALYSIS = 'analysis',
  IMAGE = 'image',
  MANEUVER = 'maneuver', // Collision avoidance maneuver plan
}

export interface WorkflowStep {
  tool: string
  status: ToolStatus
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  request: Record<string, unknown> | null
  response: Record<string, unknown> | null
  error: string | null
}

/**
 * Delta-V vector in VNB (Velocity-Normal-Binormal) frame
 */
export interface DeltaVVector {
  v_m_s: number      // Velocity (prograde) component in m/s
  n_m_s: number      // Normal component in m/s
  b_m_s: number      // Binormal component in m/s
  magnitude_m_s: number // Total magnitude in m/s
}

/**
 * Maneuver artifact data from backend collision avoidance planning.
 *
 * Per ADR-011, the only artifact is a single Mission Control HTML stored in
 * Supabase Storage. The historical visualization_urls list of secondary plots
 * was deleted; Mission Control already shows every chart a user needs.
 */
export interface ManeuverArtifactData {
  delta_v: DeltaVVector
  maneuver_time: string              // ISO datetime of maneuver execution
  predicted_miss_distance_km: number // Miss distance after maneuver in km
  original_miss_distance_km?: number // Miss distance before maneuver in km (optional)
  tca_shift_seconds: number          // Shift in TCA due to maneuver
  primary_norad_id: number           // NORAD ID of primary (maneuvering) satellite
  secondary_norad_id: number         // NORAD ID of secondary (threat) object
  mission_control_path: string       // Public Supabase Storage URL for the Mission Control HTML
}

export interface Artifact {
  type: ArtifactType
  title: string
  data_url: string | null
  data: Record<string, unknown> | ManeuverArtifactData | null
  mime_type: string | null
  created_at: string
}

export interface ChatMessage {
  id: string | null
  session_id: string
  role: MessageRole
  content: string
  workflow_steps: WorkflowStep[]
  artifacts: Artifact[]
  created_at: string
}

// WebSocket message types (discriminated union)

export enum WSMessageType {
  // Client -> Server
  CHAT = 'chat',
  CANCEL = 'cancel',

  // Server -> Client
  RESPONSE = 'response',
  ERROR = 'error',
  TOOL_START = 'tool_start',
  TOOL_COMPLETE = 'tool_complete',
  TOOL_ERROR = 'tool_error',
  ARTIFACT = 'artifact',
  STATUS = 'status',
}

export interface WSChatMessage {
  type: WSMessageType.CHAT
  content: string
  reference_time?: string | null // Sandbox mode: ISO 8601 datetime to treat as "now"
}

export interface WSCancelMessage {
  type: WSMessageType.CANCEL
}

export interface WSResponseMessage {
  type: WSMessageType.RESPONSE
  content: string
  message_id: string | null
}

export interface WSErrorMessage {
  type: WSMessageType.ERROR
  message: string
  details: Record<string, unknown> | null
}

export interface WSToolStartMessage {
  type: WSMessageType.TOOL_START
  tool: string
  request: Record<string, unknown> | null
}

export interface WSToolCompleteMessage {
  type: WSMessageType.TOOL_COMPLETE
  tool: string
  duration_ms: number
  response: Record<string, unknown> | null
}

export interface WSToolErrorMessage {
  type: WSMessageType.TOOL_ERROR
  tool: string
  error: string
}

export interface WSArtifactMessage {
  type: WSMessageType.ARTIFACT
  artifact: Artifact
}

export interface WSStatusMessage {
  type: WSMessageType.STATUS
  status: string
  details: Record<string, unknown> | null
}

// Union type for all WebSocket messages
export type WSMessage =
  | WSChatMessage
  | WSCancelMessage
  | WSResponseMessage
  | WSErrorMessage
  | WSToolStartMessage
  | WSToolCompleteMessage
  | WSToolErrorMessage
  | WSArtifactMessage
  | WSStatusMessage

// Connection state
export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
}
